import { Slot } from '@radix-ui/react-slot'
import { cn } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, LabelHTMLAttributes } from 'react'
import type { FieldError as ReactHookFormFieldError } from 'react-hook-form'
import { useFormField } from '#src/hooks/useFormField.ts'
import { forwardRef } from '#src/utils/forwardRef.ts'

const fieldVariants = cva('group/field grid gap-2 text-sm', {
  variants: {
    orientation: {
      vertical: 'gap-2',
      horizontal:
        'gap-3 md:grid md:grid-cols-[minmax(200px,auto)_1fr] md:items-start',
      responsive:
        'gap-2 sm:grid sm:grid-cols-[minmax(200px,auto)_1fr] sm:items-start sm:gap-3',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
})

export interface FieldProps
  extends
    HTMLAttributes<HTMLFieldSetElement>,
    VariantProps<typeof fieldVariants> {}

export const Field = forwardRef<HTMLFieldSetElement, FieldProps>(
  ({ className, orientation, ...props }, ref) => (
    <fieldset
      ref={ref}
      data-orientation={orientation ?? 'vertical'}
      className={cn(fieldVariants({ orientation, className }))}
      {...props}
    />
  )
)

interface FieldContentProps extends HTMLAttributes<HTMLDivElement> {}

export const FieldContent = forwardRef<HTMLDivElement, FieldContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('grid gap-1.5', className)} {...props} />
  )
)

export interface FieldGroupProps extends HTMLAttributes<HTMLDivElement> {}

export const FieldGroup = forwardRef<HTMLDivElement, FieldGroupProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('grid gap-6 md:grid-cols-2', className)}
      {...props}
    />
  )
)

interface FieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  asChild?: boolean
}

export const FieldLabel = forwardRef<HTMLLabelElement, FieldLabelProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const { error, formItemId } = useFormField()
    const Comp = asChild ? Slot : 'label'
    return (
      <Comp
        ref={ref}
        htmlFor={formItemId}
        className={cn(
          'font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
          error && 'text-destructive',
          className
        )}
        {...props}
      />
    )
  }
)

export interface FieldDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

export const FieldDescription = forwardRef<
  HTMLParagraphElement,
  FieldDescriptionProps
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()
  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
})

export interface FieldErrorProps extends HTMLAttributes<HTMLParagraphElement> {
  errors?: (ReactHookFormFieldError | string | null | undefined)[]
}

export const FieldError = forwardRef<HTMLParagraphElement, FieldErrorProps>(
  ({ className, errors, children, ...props }, ref) => {
    const { error, formErrorMessageId } = useFormField()
    const resolvedErrors =
      errors ??
      (error
        ? [
            error, // consumers passing errors should override this default
          ]
        : [])

    const messages =
      resolvedErrors
        ?.flatMap((entry) => {
          if (!entry) {
            return []
          }
          if (typeof entry === 'string') {
            return [entry]
          }
          return entry.message ? [String(entry.message)] : []
        })
        .filter((message) => message.length > 0) ?? []

    const hasChildren =
      children != null &&
      !(typeof children === 'string' && children.trim().length === 0)

    if (messages.length === 0 && !hasChildren) {
      return null
    }

    return (
      <p
        ref={ref}
        id={formErrorMessageId}
        className={cn('font-medium text-destructive text-sm', className)}
        {...props}
      >
        {messages.join(' ')}
        {messages.length > 0 && hasChildren ? ' ' : null}
        {children}
      </p>
    )
  }
)

export interface FieldSetProps extends HTMLAttributes<HTMLFieldSetElement> {}

export const FieldSet = forwardRef<HTMLFieldSetElement, FieldSetProps>(
  ({ className, ...props }, ref) => (
    <fieldset
      ref={ref}
      className={cn(
        'grid gap-3 rounded-lg border border-border p-4',
        className
      )}
      {...props}
    />
  )
)

export interface FieldLegendProps extends HTMLAttributes<HTMLLegendElement> {}

export const FieldLegend = forwardRef<HTMLLegendElement, FieldLegendProps>(
  ({ className, ...props }, ref) => (
    <legend
      ref={ref}
      className={cn('font-semibold text-muted-foreground text-sm', className)}
      {...props}
    />
  )
)

export interface FieldTitleProps extends HTMLAttributes<HTMLParagraphElement> {}

export const FieldTitle = forwardRef<HTMLParagraphElement, FieldTitleProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        'font-medium text-foreground text-sm leading-none',
        className
      )}
      {...props}
    />
  )
)
